import { call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import {
  LIST_REQUEST, listExecutionsSuccess, listExecutionsFailure,
  GET_REQUEST, getExecutionSuccess, getExecutionFailure,
  CANCEL_REQUEST, cancelExecutionSuccess, cancelExecutionFailure,
  RETRY_REQUEST, retryExecutionSuccess, retryExecutionFailure,
  STATS_REQUEST, getExecutionStatsSuccess, getExecutionStatsFailure
} from '../../actions/ingestion/executions';
import config from '../../../config';
import { getLocalUser } from '../../../utils/localUser';
import { Error } from '../../actions/error/error';
import { 
  processApiResponse, 
  handleSagaError, 
  createSuccessPayload, 
  createErrorPayload, extractErrorMessage } from '../../../utils/apiResponseHandler';

// Helper function to get headers with workspace ID
function getHeaders() {
  const userHeaders = getLocalUser();
  // getLocalUser() already includes X-WORKSPACE-ID from localStorage
  return userHeaders;
}

// List executions saga
function* listExecutionsSaga(action) {
  try {
    const headers = getHeaders();
    const { jobId, status, connectorType, page = 1, perPage = 50, sourceId, sortColumn = 'started_at', sortDirection = 'desc' } = action.payload || {};
    
    let url = `${config.API_URL}/ingestion/executions?page=${page}&per_page=${perPage}`;
    if (jobId) {
      url += `&job_id=${jobId}`;
    }
    if (status) {
      url += `&status=${status}`;
    }
    if (sourceId) {
      url += `&source_id=${sourceId}`;
    }
    if (connectorType) {
      url += `&connector_type=${connectorType}`;
    }
    if (sortColumn) {
      url += `&sort_column=${sortColumn}`;
    }
    if (sortDirection) {
      url += `&sort_direction=${sortDirection}`;
    }
    
    const response = yield call(
      axios.get,
      url,
      { headers }
    );
    
    // Handle nested data structure from API
    const executionsData = response.data?.data || response.data;
    yield put(listExecutionsSuccess(executionsData));
  } catch (error) {
    yield put(Error(error));
    yield put(listExecutionsFailure(extractErrorMessage(error)));
  }
}

// Get execution details saga
function* getExecutionSaga(action) {
  try {
    const headers = getHeaders();
    const { executionId } = action.payload;
    
    const response = yield call(
      axios.get,
      `${config.API_URL}/ingestion/executions/${executionId}`,
      { headers }
    );
    
    // Handle nested data structure from API
    const executionData = response.data?.data || response.data;
    yield put(getExecutionSuccess(executionData));
  } catch (error) {
    yield put(Error(error));
    yield put(getExecutionFailure(extractErrorMessage(error)));
  }
}

// Cancel execution saga
function* cancelExecutionSaga(action) {
  try {
    const headers = getHeaders();
    const { executionId } = action.payload;
    
    const response = yield call(
      axios.post,
      `${config.API_URL}/ingestion/executions/${executionId}/cancel`,
      {},
      { headers }
    );
    
    yield put(cancelExecutionSuccess({ executionId, ...response.data }));
    yield put({ type: 'SHOW_SUCCESS_SNACKBAR', message: 'Execution cancellation requested' });
  } catch (error) {
    yield put(Error(error));
    yield put(cancelExecutionFailure(extractErrorMessage(error)));
    yield put({ type: 'SHOW_ERROR_SNACKBAR', message: 'Failed to cancel execution' });
  }
}

// Retry execution saga
function* retryExecutionSaga(action) {
  try {
    const headers = getHeaders();
    const { executionId } = action.payload;
    
    const response = yield call(
      axios.post,
      `${config.API_URL}/ingestion/executions/${executionId}/retry`,
      {},
      { headers }
    );
    
    yield put(retryExecutionSuccess({ executionId, ...response.data }));
    yield put({ type: 'SHOW_SUCCESS_SNACKBAR', message: 'Execution retry queued' });
  } catch (error) {
    yield put(Error(error));
    yield put(retryExecutionFailure(extractErrorMessage(error)));
    yield put({ type: 'SHOW_ERROR_SNACKBAR', message: 'Failed to retry execution' });
  }
}

// Get execution stats saga
function* getExecutionStatsSaga(action) {
  try {
    const headers = getHeaders();
    
    const response = yield call(
      axios.get,
      `${config.API_URL}/ingestion/executions/stats`,
      { headers }
    );
    
    yield put(getExecutionStatsSuccess(response.data));
  } catch (error) {
    yield put(Error(error));
    yield put(getExecutionStatsFailure(extractErrorMessage(error)));
  }
}

// Watcher saga
export function* watch() {
  yield takeLatest(LIST_REQUEST, listExecutionsSaga);
  yield takeLatest(GET_REQUEST, getExecutionSaga);
  yield takeLatest(CANCEL_REQUEST, cancelExecutionSaga);
  yield takeLatest(RETRY_REQUEST, retryExecutionSaga);
  yield takeLatest(STATS_REQUEST, getExecutionStatsSaga);
}
